import { Redirect, useParams } from "react-router-dom";

import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import CustomerTabs from "@/components/CustomerTabs";
import DataLoader from "@/components/DataLoader";
import GenesBiomarkers from "@/components/GeneBiomarkers";
import LabReports from "@/components/LabReports";
import CardListContext from "@/contexts/CardListContext";
import { REVIEW_STATUS_APPROVED } from "@/core/constants";
import { DASHBOARD_HOME_URL, ERROR_404_URL } from "@/core/urls";
import useCardList from "@/hooks/useCardList";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Center from "@/tpo/Center";
import ChartModal from "@/tpo/ChartModal";
import Spacer from "@/tpo/Spacer";
import TestStatuses, { getUserTestOrderId } from "@/tpo/TestStatuses";
import B2CHeader from "@/tpo/header/B2CHeader";
import { useTheme } from "styled-components";

import ModelContent from "./ModelContent";
import RelatedReportsSection from "./RelatedReportsSection";
import {
  GENES_TEST_BIOMARKERS_CONNECTION_QUERY,
  LAB_REPORTS_QUERY,
  USER_TEST_QUERY,
  USER_TESTS_AND_ORDERS_QUERY
} from "./graphql";
import useCustomerTestsAndReports from "./useCustomerTestsAndReports";

function UserTestContent({ userTest }) {
  const theme = useTheme();
  const { cardOpen, closeCard, viewCard, setCardOpen } = useCardList();
  const customerTestsAndReports = useCustomerTestsAndReports();
  const displayOrderId = userTest ? getUserTestOrderId(userTest) : null;
  if (userTest?.reviewStatus === "Processing") {
    return <Redirect to={DASHBOARD_HOME_URL} />;
  }

  if (userTest === undefined) {
    return <Redirect to={ERROR_404_URL} />;
  }

  return (
    <>
      <PanelBoxV2
        outer={{
          bg: "white",
          pt: [30, 30, 60],
          pb: [50, 50, 80]
        }}
      >
        <PanelBoxV2
          maxWidth={[464, 464, 464, 500]}
          outer={{
            px: [20, null]
          }}
        >
          <DataLoader
            query={USER_TESTS_AND_ORDERS_QUERY}
            variables={{
              allTests: true,
              excludeRetests: true
            }}
            render={({ userTests, ordersInProgress }) => {
              return !!userTests.length || !!ordersInProgress.length ? (
                <TestStatuses
                  heading="Available tests"
                  ordersInProgress={ordersInProgress}
                  testResults={userTests}
                  {...customerTestsAndReports}
                  defaultOpen={false}
                />
              ) : (
                <TestStatuses heading="No tests" />
              );
            }}
          />
        </PanelBoxV2>
        <Spacer py={[2, 2, 20]} />
      </PanelBoxV2>
      <PanelBoxV2
        outer={{
          pt: theme.spacing.section.pt,
          pb: theme.spacing.section.pb,
          px: [20, 20, "5.5vw"]
        }}
        stacked
        gap={40}
      >
        <Center stacked>
          <Box fontFamily="gilroyBold" fontSize={[24, 24, 44]} textAlign="center">
            {userTest?.name}
          </Box>
          {displayOrderId && (
            <Box
              fontFamily="gilroyBold"
              lineHeight="36px"
              textAlign="center"
              fontSize={[18, 18, 24]}
              mb={20}
            >
              {displayOrderId}
            </Box>
          )}
          <Box textAlign="center">
            {userTest?.earliestDateExtracted ? (
              <>
                <Box as="span" fontFamily="gilroyBold">
                  Sample taken:
                </Box>
                <Box as="span" fontFamily="gilroyMedium" fontSize={16} ml={1}>
                  {new Date(userTest?.earliestDateExtracted).toLocaleDateString()}
                </Box>
              </>
            ) : (
              <>
                <Box as="span" fontFamily="gilroyBold">
                  Results uploaded:
                </Box>
                <Box as="span" fontFamily="gilroyMedium" fontSize={16} ml={1}>
                  {new Date(userTest?.created).toLocaleDateString()}
                </Box>
              </>
            )}
          </Box>
        </Center>

        <RelatedReportsSection userTest={userTest} />
        <LabReports
          query={LAB_REPORTS_QUERY}
          userTest={userTest}
          variables={{
            forPractitioner: false
          }}
        />
        {userTest.isGenesTest && userTest.reviewStatus === REVIEW_STATUS_APPROVED ? (
          <>
            <CardListContext.Provider
              value={{
                cardOpen,
                closeCard,
                viewCard,
                setCardOpen
              }}
            >
              <GenesBiomarkers userTest={userTest} query={GENES_TEST_BIOMARKERS_CONNECTION_QUERY} />
            </CardListContext.Provider>
            <ChartModal close={() => setCardOpen(undefined)} show={!!cardOpen?.type}>
              <ModelContent
                type={cardOpen?.type}
                item={cardOpen?.item}
                isGenesTest={userTest.isGenesTest}
              />
            </ChartModal>
          </>
        ) : null}
      </PanelBoxV2>
    </>
  );
}

export default function Test() {
  const { userTestId } = useParams();

  return (
    <>
      <B2CHeader />
      <CustomerPage
        bg="haze"
        includeFooter
        jumbotronProps={{
          title: "Test Results"
        }}
      >
        <CustomerTabs selectedTab="/test-results" />
        <DataLoader
          key={userTestId}
          query={USER_TEST_QUERY}
          variables={{
            userTestId
          }}
          render={({ userTest }) => <UserTestContent userTest={userTest} />}
        />
      </CustomerPage>
    </>
  );
}
